import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ open, items, index, onClose, onPrev, onNext }) {
    const handleKey = useCallback(
        (e) => {
            if (!open) return;
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowLeft") onPrev();
            else if (e.key === "ArrowRight") onNext();
        },
        [open, onClose, onPrev, onNext],
    );

    useEffect(() => {
        if (!open) return undefined;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKey);
        };
    }, [open, handleKey]);

    if (!open || !items || items.length === 0) return null;
    const item = items[index] || items[0];

    return (
        <div
            data-testid="lightbox"
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-8 fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={item.title || "Imagen ampliada"}
        >
            <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                data-testid="lightbox-close"
                aria-label="Cerrar"
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
                <X className="w-5 h-5" />
            </button>

            {items.length > 1 && (
                <>
                    <button
                        onClick={(e) => { e.stopPropagation(); onPrev(); }}
                        data-testid="lightbox-prev"
                        aria-label="Imagen anterior"
                        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onNext(); }}
                        data-testid="lightbox-next"
                        aria-label="Imagen siguiente"
                        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>
                </>
            )}

            <div className="relative max-w-6xl w-full max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                <img
                    src={item.img}
                    alt={item.title || ""}
                    className="max-h-[80vh] w-auto object-contain rounded-sm shadow-2xl"
                />
                <div className="mt-5 text-center text-white max-w-2xl">
                    {item.type && (
                        <span className="inline-block font-display font-bold text-[10px] tracking-[0.25em] uppercase px-2.5 py-1 bg-eclipse-cta mb-3">
                            {item.type}
                        </span>
                    )}
                    {item.title && <h3 className="font-display font-bold text-xl sm:text-2xl">{item.title}</h3>}
                    {(item.city || item.meta) && (
                        <p className="text-sm text-white/75 mt-1">{[item.meta, item.city].filter(Boolean).join(" · ")}</p>
                    )}
                    <p className="text-xs text-white/40 mt-4">
                        {index + 1} / {items.length} · Esc para cerrar · ← → para navegar
                    </p>
                </div>
            </div>
        </div>
    );
}
